import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { 
  LayoutDashboard, 
  CalendarDays, 
  BarChart3, 
  Download,
  Zap,
  LogOut,
  User,
  MessageCircle,
  Users,
  Menu,
  X,
  RefreshCw,
  Sun,
  Moon,
  Heart,
  Target
} from "lucide-react";

// Configure axios to include incognito ID for anonymous users
axios.interceptors.request.use((config) => {
  const incognitoId = localStorage.getItem("moodpunk_incognito_id");
  const authMode = localStorage.getItem("moodpunk_auth_mode");
  if (incognitoId && authMode === "incognito") {
    config.headers["X-Incognito-ID"] = incognitoId;
  }
  return config;
});

// Auth, Language & Theme
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { MoodThemeProvider, useMoodTheme } from "./contexts/MoodThemeContext";
import { LoginPage } from "./components/LoginPage";
import { AuthCallback } from "./components/AuthCallback";

// Components
import { GlassCard } from "./components/GlassCard";
import { MoodSelector } from "./components/MoodSelector";
import { MoodCalendar } from "./components/MoodCalendar";
import { MoodCharts } from "./components/MoodCharts";
import { ExportPanel } from "./components/ExportPanel";
import { StatsPanel } from "./components/StatsPanel";
import { RecentMoods } from "./components/RecentMoods";
import { AIInsights } from "./components/AIInsights";
import { ChatPage } from "./components/ChatPage";
import { ProfilePage } from "./components/ProfilePage";
import { NotificationBellButton, useNotificationReminder } from "./components/NotificationReminder";
import { usePullToRefresh, PullToRefreshIndicator } from "./components/PullToRefresh";
import { AIChatbot } from "./components/AIChatbot";
import { GamificationPanel } from "./components/GamificationPanel";
import { GratitudeJournal } from "./components/GratitudeJournal";
import { WeatherWidget } from "./components/WeatherWidget";
import { DailyChallenges } from "./components/DailyChallenges";
import { MeditationPage } from "./components/MeditationPage";
import { WellnessPage } from "./components/WellnessPage";
import { WellnessProfileWidget } from "./components/WellnessProfileWidget";
import { UpgradePrompt, UpgradeBanner, UpgradeShowcase } from "./components/UpgradePrompt";
import { ChallengesPage } from "./components/ChallengesPage";
import { MoodAtmosphere, MoodGarden, MoodWheel, MoodThemeSettings } from "./components/MoodAtmosphere";
import { MoodStory, MoodStoryButton } from "./components/MoodStory";
import { DailyQuoteModal, DailyQuoteWidget } from "./components/DailyQuote";
import { MoodPunkLogo } from "./components/MoodPunkLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// User Count Component
const UserCount = () => {
  const [count, setCount] = useState(null);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await axios.get(`${API}/users/count`);
        setCount(response.data.count);
      } catch (error) {
        console.error("Error fetching user count:", error);
      }
    };
    fetchCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  if (count === null) return null;

  return (
    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neon-purple/10 border border-neon-purple/30" data-testid="user-count">
      <Users className="w-4 h-4 text-neon-purple" />
      <span className="font-orbitron text-sm text-neon-purple">{count.toLocaleString()}</span>
      <span className="text-xs text-slate-500 font-rajdhani hidden sm:inline">usuarios</span>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, authMode, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Different nav items for incognito vs registered users
  const navItems = authMode === "incognito" 
    ? [
        { path: "/", icon: LayoutDashboard, label: "Dashboard" },
        { path: "/calendar", icon: CalendarDays, label: "Calendario" },
        { path: "/stats", icon: BarChart3, label: "Stats" },
      ]
    : [
        { path: "/", icon: LayoutDashboard, label: "Dashboard" },
        { path: "/challenges", icon: Target, label: "Retos" },
        { path: "/chat", icon: MessageCircle, label: "Chat", showBadge: true },
        { path: "/calendar", icon: CalendarDays, label: "Calendario" },
        { path: "/stats", icon: BarChart3, label: "Stats" },
        { path: "/wellness", icon: Heart, label: "Bienestar" },
        { path: "/meditations", icon: Users, label: "Meditar" },
        { path: "/profile", icon: User, label: "Perfil" },
      ];

  // Fetch unread message count
  useEffect(() => {
    if (authMode === "incognito") return;
    
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get(`${API}/messages/unread-count`);
        setUnreadCount(response.data.unread_count || 0);
      } catch (error) {
        console.error("Error fetching unread count:", error);
      }
    };

    fetchUnreadCount();
    // Poll every 10 seconds
    const interval = setInterval(fetchUnreadCount, 10000);
    return () => clearInterval(interval);
  }, [authMode]);

  const handleLogout = async () => {
    await logout();
    toast.success("Sesión cerrada");
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <nav className="glass-card px-4 py-3 mb-6">
      <div className="flex items-center justify-between gap-4">
        {/* Logo */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Zap className="w-7 h-7 text-neon-cyan" />
          <span className="hidden sm:block">
            <MoodPunkLogo size="sm" />
          </span>
        </div>
        
        {/* Desktop Navigation - Centered */}
        <div className="hidden lg:flex items-center justify-center flex-1">
          <div className="flex gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative flex items-center gap-1 px-2.5 py-1.5 rounded-lg font-rajdhani font-semibold tracking-wide uppercase text-xs transition-all duration-300 ${
                    isActive || (item.path === "/" && location.pathname === "/")
                      ? "bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/50"
                      : "text-slate-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <item.icon className="w-3.5 h-3.5" />
                <span>{item.label}</span>
                {item.showBadge && unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] flex items-center justify-center bg-neon-purple text-white text-[9px] font-bold rounded-full px-0.5">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* User Count */}
          <UserCount />

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            title={isDark ? "Tema claro" : "Tema oscuro"}
            className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
          >
            {isDark ? (
              <Sun className="w-4 h-4 text-neon-yellow" />
            ) : (
              <Moon className="w-4 h-4 text-neon-purple" />
            )}
          </button>

          {/* Notification Bell */}
          <NotificationBellButton />

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-4 h-4 text-slate-400" />
            ) : (
              <Menu className="w-4 h-4 text-slate-400" />
            )}
          </button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                data-testid="user-menu-btn"
              >
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-5 h-5 rounded-full"
                  />
                ) : (
                  <User className="w-4 h-4 text-slate-400" />
                )}
                <span className="hidden xl:inline text-xs text-slate-300 font-rajdhani max-w-[60px] truncate">
                  {user?.name?.split(" ")[0] || "Usuario"}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-void-paper border border-white/10 text-slate-300">
              <DropdownMenuLabel className="font-rajdhani">
                {authMode === "google" ? (
                  <div>
                    <p className="text-white">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                    <p className="text-xs text-neon-cyan">ID: {user?.user_id}</p>
                  </div>
                ) : authMode === "moodpunk" ? (
                  <div>
                    <p className="text-white">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                    <p className="text-xs text-neon-purple">@{user?.user_id}</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-neon-purple">Modo Incógnito</span>
                  </div>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              
              <DropdownMenuItem 
                onClick={() => navigate('/export')}
                className="cursor-pointer focus:bg-white/10"
                data-testid="export-menu-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar Datos
              </DropdownMenuItem>
              
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 cursor-pointer focus:text-red-400 focus:bg-red-400/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="sm:hidden mt-4 pt-4 border-t border-white/10"
          >
            <div className="grid grid-cols-5 gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `relative flex flex-col items-center gap-1 p-2 rounded-lg font-rajdhani text-xs transition-all ${
                      isActive || (item.path === "/" && location.pathname === "/")
                        ? "bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/50"
                        : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span className="truncate">{item.label}</span>
                  {item.showBadge && unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center bg-neon-purple text-white text-[10px] font-bold rounded-full px-1">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, initialized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (initialized && !isAuthenticated) {
      navigate("/login", { replace: true, state: { from: location } });
    }
  }, [initialized, isAuthenticated, navigate, location]);

  if (loading || !initialized) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
};

// Dashboard Page
const Dashboard = () => {
  const { authMode } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentMoods, setRecentMoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, moodsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/moods?limit=20`)
      ]);
      setStats(statsRes.data);
      setRecentMoods(moodsRes.data);
      setLastSync(new Date());
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMoodSaved = () => {
    fetchData();
    toast.success("¡Estado de ánimo registrado!");
  };

  // Manual sync button handler
  const handleManualSync = async () => {
    setSyncing(true);
    toast.info("Sincronizando datos...");
    try {
      await fetchData();
      toast.success("¡Datos sincronizados correctamente!");
    } catch (error) {
      toast.error("Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  // Pull to refresh handler
  const handleRefresh = useCallback(async () => {
    toast.info("Actualizando...");
    await fetchData();
    toast.success("¡Datos actualizados!");
  }, [fetchData]);

  const { isPulling, pullDistance, isRefreshing } = usePullToRefresh(handleRefresh);

  // Format last sync time
  const formatLastSync = () => {
    if (!lastSync) return null;
    const now = new Date();
    const diff = Math.floor((now - lastSync) / 1000); // seconds
    
    if (diff < 10) return "Ahora";
    if (diff < 60) return `Hace ${diff}s`;
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)}h`;
    return lastSync.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  // Update last sync display every 10 seconds
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => forceUpdate(n => n + 1), 10000);
    return () => clearInterval(interval);
  }, []);

  // Mood Story state
  const [showMoodStory, setShowMoodStory] = useState(false);

  // Daily Quote state - show on first visit of the day
  const [showDailyQuote, setShowDailyQuote] = useState(false);

  // Check if we should show the daily quote (once per day per session)
  useEffect(() => {
    if (authMode === "incognito") return;
    
    const today = new Date().toDateString();
    const lastQuoteDate = sessionStorage.getItem("moodpunk_last_quote_date");
    
    if (lastQuoteDate !== today) {
      // Show quote after a small delay for better UX
      const timer = setTimeout(() => {
        setShowDailyQuote(true);
        sessionStorage.setItem("moodpunk_last_quote_date", today);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [authMode]);

  return (
    <>
      {/* Daily Quote Modal */}
      <DailyQuoteModal 
        isOpen={showDailyQuote} 
        onClose={() => setShowDailyQuote(false)}
        recentMood={recentMoods[0]?.mood}
      />

      {/* Mood Story Modal */}
      <AnimatePresence>
        {showMoodStory && (
          <MoodStory onClose={() => setShowMoodStory(false)} />
        )}
      </AnimatePresence>

      <PullToRefreshIndicator 
        pullDistance={pullDistance} 
        isRefreshing={isRefreshing} 
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4 }}
        data-testid="dashboard-page"
        className="space-y-6"
        style={{ 
          transform: isPulling ? `translateY(${pullDistance * 0.3}px)` : 'none',
          transition: isPulling ? 'none' : 'transform 0.3s ease-out'
        }}
      >
      {/* Main Grid - Control Room Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {/* Mood Selector - Main Panel */}
        <div className="lg:col-span-2 xl:col-span-3">
          <GlassCard className="p-6 xl:p-8 h-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-cyber text-xl sm:text-2xl xl:text-3xl">¿Cómo te sientes ahora mismo?</h2>
              <div className="flex items-center gap-2">
                {lastSync && (
                  <span className="text-xs text-slate-500 font-rajdhani hidden sm:inline" data-testid="last-sync">
                    {formatLastSync()}
                  </span>
                )}
                {/* Mood Story Button - only for registered users */}
                {authMode !== "incognito" && (
                  <MoodStoryButton onClick={() => setShowMoodStory(true)} />
                )}
                <button
                  onClick={handleManualSync}
                  disabled={syncing}
                  data-testid="sync-btn"
                  title={lastSync ? `Última sync: ${formatLastSync()}` : 'Sincronizar datos'}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg font-rajdhani text-sm font-semibold transition-all
                    ${syncing 
                      ? 'bg-neon-cyan/10 text-neon-cyan/50 cursor-not-allowed' 
                      : 'bg-neon-cyan/20 text-neon-cyan hover:bg-neon-cyan/30 border border-neon-cyan/30 hover:border-neon-cyan/50'
                    }`}
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">{syncing ? 'Sincronizando...' : 'Sincronizar'}</span>
                </button>
              </div>
            </div>
            
            {/* Daily Quote Widget - above mood selector */}
            {authMode !== "incognito" && (
              <DailyQuoteWidget mood={recentMoods[0]?.mood} className="mb-6" />
            )}
            
            <MoodSelector onMoodSaved={handleMoodSaved} />
          </GlassCard>
        </div>

        {/* Stats Panel */}
        <div className="space-y-6">
          <WeatherWidget compact={false} />
          <StatsPanel stats={stats} loading={loading} />
        </div>
      </div>

      {/* Gamification & Gratitude Row - Only for registered users */}
      {authMode !== "incognito" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          <DailyChallenges compact />
          <GamificationPanel />
          <GratitudeJournal />
        </div>
      ) : (
        <UpgradeShowcase />
      )}

      {/* Recent Moods */}
      <GlassCard className="p-6 xl:p-8">
        <h2 className="heading-cyber text-xl xl:text-2xl mb-4">Registro Reciente</h2>
        <RecentMoods moods={recentMoods} loading={loading} onUpdate={fetchData} />
      </GlassCard>

      {/* Mood Garden - Visual Timeline - Only for registered users with moods */}
      {authMode !== "incognito" && recentMoods.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GlassCard className="p-6">
            <h3 className="heading-cyber text-lg mb-4 flex items-center gap-2">
              <span className="text-2xl">🌱</span>
              Jardín Emocional
            </h3>
            <MoodGarden moods={recentMoods} maxItems={5} />
          </GlassCard>
          <GlassCard className="p-6 flex flex-col items-center justify-center">
            <h3 className="heading-cyber text-lg mb-4">Tu Estado Actual</h3>
            <MoodWheel currentMood={recentMoods[0]?.mood} size={220} />
          </GlassCard>
        </div>
      )}

      {/* AI Insights - shown only on smaller screens for registered users */}
      {authMode !== "incognito" && (
        <div className="xl:hidden">
          <AIInsights />
        </div>
      )}
    </motion.div>
    </>
  );
};

// Calendar Page
const CalendarPage = () => {
  const [moods, setMoods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMoods = async () => {
      try {
        const response = await axios.get(`${API}/moods?limit=365`);
        setMoods(response.data);
      } catch (error) {
        console.error("Error fetching moods:", error);
        toast.error("Error al cargar el calendario");
      } finally {
        setLoading(false);
      }
    };
    fetchMoods();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      data-testid="calendar-page"
    >
      <GlassCard className="p-6">
        <h2 className="heading-cyber text-2xl mb-6">Calendario de Estados de Ánimo</h2>
        <MoodCalendar moods={moods} loading={loading} />
      </GlassCard>
    </motion.div>
  );
};

// Stats Page
const StatsPage = () => {
  const [moods, setMoods] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [moodsRes, statsRes] = await Promise.all([
          axios.get(`${API}/moods?limit=90`),
          axios.get(`${API}/stats`)
        ]);
        setMoods(moodsRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
        toast.error("Error al cargar las estadísticas");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      data-testid="stats-page"
      className="space-y-6"
    >
      <GlassCard className="p-6">
        <h2 className="heading-cyber text-2xl mb-6">Análisis de Estados de Ánimo</h2>
        <MoodCharts moods={moods} stats={stats} loading={loading} />
      </GlassCard>
    </motion.div>
  );
};

// Export Page
const ExportPage = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      data-testid="export-page"
    >
      <GlassCard className="p-6">
        <h2 className="heading-cyber text-2xl mb-6">Exportar Datos</h2>
        <ExportPanel />
      </GlassCard>
    </motion.div>
  );
};

// Main Layout
const Layout = ({ children }) => {
  const { authMode } = useAuth();
  
  // Initialize notification reminders
  useNotificationReminder();
  
  return (
    <div className="min-h-screen bg-void relative overflow-hidden">
      {/* Mood-based atmospheric effects */}
      <MoodAtmosphere intensity="medium" />
      
      {/* Background effects */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-neon-purple/10 via-transparent to-transparent pointer-events-none" />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-neon-cyan/5 via-transparent to-transparent pointer-events-none" />
      
      {/* Scanlines overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.02]" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.1), rgba(255,255,255,0.1) 1px, transparent 1px, transparent 2px)'
      }} />
      
      <div className="relative z-10 w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-6">
        <Navigation />
        <main>
          <AnimatePresence mode="wait">
            {children}
          </AnimatePresence>
        </main>
      </div>
      
      {/* AI Chatbot - floating button - Only for registered users */}
      {authMode !== "incognito" && <AIChatbot />}
    </div>
  );
};

// App Router - handles auth callback detection
const AppRouter = () => {
  const location = useLocation();
  const { authMode } = useAuth();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.pathname === "/auth/callback" || location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Component for routes that require registration
  const RegisteredOnlyRoute = ({ children, feature, description }) => {
    if (authMode === "incognito") {
      return (
        <Layout>
          <UpgradePrompt feature={feature} description={description} />
        </Layout>
      );
    }
    return <Layout>{children}</Layout>;
  };

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <RegisteredOnlyRoute 
              feature="Chat entre usuarios" 
              description="Conecta con otros usuarios y comparte tu estado emocional"
            >
              <ChatPage />
            </RegisteredOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/challenges"
        element={
          <ProtectedRoute>
            <RegisteredOnlyRoute
              feature="Retos Diarios"
              description="Completa retos de bienestar para ganar XP y competir con otros usuarios"
            >
              <ChallengesPage />
            </RegisteredOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <Layout>
              <CalendarPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stats"
        element={
          <ProtectedRoute>
            <Layout>
              <StatsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/export"
        element={
          <ProtectedRoute>
            <RegisteredOnlyRoute
              feature="Exportar Datos"
              description="Descarga tu historial emocional en CSV o JSON"
            >
              <ExportPage />
            </RegisteredOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <RegisteredOnlyRoute
              feature="Perfil de Usuario"
              description="Personaliza tu experiencia y gestiona tu cuenta"
            >
              <ProfilePage />
            </RegisteredOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/meditations"
        element={
          <ProtectedRoute>
            <RegisteredOnlyRoute
              feature="Meditaciones Guiadas"
              description="Accede a meditaciones y contenido de bienestar"
            >
              <MeditationPage />
            </RegisteredOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wellness"
        element={
          <ProtectedRoute>
            <RegisteredOnlyRoute
              feature="Bienestar Personalizado"
              description="Recibe recomendaciones de IA sobre alimentación, ejercicio y salud hormonal"
            >
              <WellnessPage />
            </RegisteredOnlyRoute>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <ThemeProvider>
          <MoodThemeProvider>
            <AuthProvider>
              <Toaster 
                position="top-right" 
                toastOptions={{
                  style: {
                    background: '#0a0a0f',
                    border: '1px solid rgba(0, 243, 255, 0.3)',
                    color: '#e2e8f0',
                  },
                }}
              />
              <AppRouter />
            </AuthProvider>
          </MoodThemeProvider>
        </ThemeProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
